/**
 * @file        sh.js           Module holding routines for interfacing cleanly with /bin/sh
 * @author      Wes Garland, wes@kingsds.network
 * @date        April 2020
 */
'use strict';

const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const env = require('dcp/common/dcp-env');
const { assert } = require('dcp/common/dcp-assert');

/**
 * CURRENTLY UNUSED
 * Parse a line into tokens, similar to the Bourne shell's `set` command.
 * The following syntax is supported:
 * - backslash to escape - supported escape are double quote, space, single quote
 * - space as the token separator (IFS)
 * - double quotes to quote token groups that can contain escapes
 * - single quotes to quote token groups that cannot contain escapes
 * - empty tokens are ignored
 *
 * @param    {string}  inBuf           The string to parse or falsey
 * @returns  {array} array of parsed tokens
 */
exports.tokenize = function utils$$tokenize(inBuf) {
  var outBuf = '';
  var outBufs = [];
  var inSQSection = false;
  var inDQSection = false;
  var escape = false;

  if (!inBuf)
    return outBufs;

  for (let i=0; i < inBuf.length; i++) {
    escape = false;
    if (!inSQSection && inBuf[i] === '\\') {
      switch(inBuf[i + 1]) {
        case '"': case ' ': case "'":
          escape = true;
          i++;
          break;
      }
    }

    if (!inDQSection && !inSQSection && !escape && inBuf[i] === ' ') {
      if (outBuf.length)
        outBufs.push(outBuf);
      outBuf = '';
      continue;
    }

    if (!escape && !inSQSection && inBuf[i] === '"') {
      inDQSection = !inDQSection;
      continue;
    }

    if (!escape && !inDQSection && inBuf[i] === "'") {
      inSQSection = !inSQSection;
      continue;
    }

    outBuf += inBuf[i];
  }

  if (outBuf)
    outBufs.push(outBuf);

  return outBufs;
}

/** Resolve pathnames via usual bash tilde-expand rules. Does not glob.
 */
exports.expandPath = function utils$$sh$expandPath(pathname)
{
  var re, match, self;

  if (env.isBrowserPlatform)
    return pathname;

  if (env.platform === 'pythonmonkey')
    return globalThis.python.eval(`__import__("os").path.expanduser("${pathname}")`);

  assert(env.platform === 'nodejs');

  const os = requireNative('os');
  const path = requireNative('path');

  if (!pathname)
    return pathname;

  if (path.isAbsolute(pathname))
    return pathname;

  if (pathname === '~')
    return os.homedir();

  if (pathname === '~+')
    return process.cwd();

  if (pathname.startsWith('~' + path.sep))
    return path.resolve(os.homedir(), pathname.slice(2));

  self = os.userInfo.username;

  re = new RegExp('~([^\\/]+)([\\/]|$)(.*)');
  if ((match = re.exec(pathname))) {
    let user = match[1];
    let rest = match[3];

    if (user === self)
      return path.resolve(os.homedir(), rest);

    return path.resolve(exports.locateHomeDir(user), rest);
  }

  if (pathname.startsWith('.' + path.sep) || pathname.startsWith('..' + path.sep))
    return path.resolve(process.cwd(), pathname);

  return path.resolve(pathname);
}

/** Locate a given user's home directory.  For nodejs, we guess on Win32 and read
 *  the passwd file on Unix. In Pythonmonkey, we do what Python does.
 *
 * @todo  This is fine for a fall-back, but we should be using node's FFI
 *        to invoke getpwent in the fullness of time. /wg feb 2021
 *
 * @returns {string|null}   string which is a fully-resolved path to the user's homedir, or null when
 *                          the user does not exist.
 */
exports.locateHomeDir = function utils$$sh$locateHomeDir(username)
{
  if (env.isBrowserPlatform)
    throw new Error('home directories do not exist on browser platforms');

  if (env.platform === 'pythonmonkey')
    return globalThis.python.eval(`__import__("os").path.expanduser("~${username}")`);

  assert(env.platform === 'nodejs');

  const os   = requireNative('os');
  const fs   = requireNative('fs');
  const path = requireNative('path');

  if (os.platform() === 'win32')
    return path.resolve(os.homedir(), '..', username);

  const epw = fs.readFileSync('/etc/passwd', 'ascii');
  const entries = epw.split('\n');
  const names = entries.map((a) => a.replace(/:.*/,''));
  const idx = names.indexOf(username);

  if (idx === -1)
    return null;

  return path.resolve('/', entries[idx].split(':')[5]);
}
